package fr.manyverse.app.domain.validation

import fr.manyverse.app.domain.error.ValidationError
import fr.manyverse.app.domain.model.AudioData

/**
 * Validator for audio data.
 * Validates size limits and MIME types for sound uploads.
 */
object AudioValidator {
    private const val MAX_SIZE_BYTES = 50 * 1024 * 1024 // 50MB
    private const val MAX_NAME_LENGTH = 100
    private val ALLOWED_MIME_TYPES = setOf(
        "audio/mpeg",
        "audio/mp3",
        "audio/wav",
        "audio/x-wav",
        "audio/ogg",
        "audio/vorbis",
        "audio/flac",
        "audio/x-flac",
        "audio/aac",
        "audio/mp4",
        "audio/x-m4a",
        "audio/m4a"
    )

    /**
     * Validates audio data for upload.
     * @param audioData The audio data to validate
     * @return Result.success if valid, Result.failure with ValidationError if invalid
     */
    fun validate(audioData: AudioData): Result<Unit> {
        if (audioData.bytes.size > MAX_SIZE_BYTES) {
            return Result.failure(
                ValidationError("Audio exceeds maximum size of 50MB")
            )
        }
        if (audioData.mimeType !in ALLOWED_MIME_TYPES) {
            return Result.failure(
                ValidationError("Invalid audio type. Allowed: MP3, WAV, OGG, FLAC, AAC")
            )
        }
        return Result.success(Unit)
    }

    /**
     * Validates sound name.
     * @param name The sound name to validate
     * @return Result.success if valid, Result.failure with ValidationError if invalid
     */
    fun validateName(name: String): Result<Unit> {
        val trimmedName = name.trim()
        if (trimmedName.isEmpty()) {
            return Result.failure(
                ValidationError("Sound name is required")
            )
        }
        if (trimmedName.length > MAX_NAME_LENGTH) {
            return Result.failure(
                ValidationError("Sound name must be $MAX_NAME_LENGTH characters or less")
            )
        }
        return Result.success(Unit)
    }
}
